/**
 * Админка справочника — раздел Mini App для управляющего (§11.1).
 *
 * Экран отвечает на главный риск сервиса: **карточки останутся пустыми**
 * (R1). Поэтому он устроен не как «CRUD-таблица», а как рабочее место
 * наполнения:
 *
 * - список сразу показывает **пробелы** («нет фото», «нет состава»), а не
 *   только названия: искать дыры отдельным отчётом никто не будет;
 * - завести карточку можно из одного поля — обязательно только название
 *   (§11.3), остальное дозаполняется потом;
 * - у опубликованной карточки правка уходит в черновик, и об этом прямо
 *   сказано в интерфейсе: человек должен понимать, почему его текст ещё
 *   не виден официантам (§11.5).
 *
 * Права проверяет сервер на каждом запросе (§11.1). Скрытие раздела —
 * оформление: `403` придёт и тому, кто откроет адрес руками.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Field, Loading, Select } from '@shell/index';
import { studyApi } from '../api';
import type { AdminDishRow } from '../api';

function Holes({ row }: { row: AdminDishRow }) {
  const problems: string[] = [];
  if (row.photos === 0) problems.push('нет фото');
  if (row.ingredients === 0) problems.push('нет состава');
  if (!row.has_text) problems.push('нет описания');
  if (problems.length === 0) {
    return <span className="faint">заполнена</span>;
  }
  return <span className="faint">{problems.join(' · ')}</span>;
}

export function Admin() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [menuId, setMenuId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const dishes = useQuery({ queryKey: ['admin-dishes'], queryFn: studyApi.adminDishes });
  const report = useQuery({
    queryKey: ['incomplete'],
    queryFn: studyApi.incompleteReport,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-dishes'] });
    void queryClient.invalidateQueries({ queryKey: ['incomplete'] });
    // Справочник официанта тоже меняется — публикация видна сразу.
    void queryClient.invalidateQueries({ queryKey: ['dishes'] });
  };

  const create = useMutation({
    mutationFn: () =>
      studyApi.createDish({
        name: name.trim(),
        menu_id: menuId ?? (dishes.data?.menus[0]?.id as number),
      }),
    onSuccess: () => {
      setNotice(`Черновик «${name.trim()}» заведён. Опубликуйте, когда заполните.`);
      setName('');
      refresh();
    },
  });

  const publish = useMutation({
    mutationFn: (id: number) => studyApi.publishDish(id),
    onSuccess: () => {
      setNotice('Опубликовано — карточка видна сотрудникам.');
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => studyApi.deleteDish(id),
    onSuccess: () => {
      setNotice('Карточка снята. История просмотров и вопросы тестов сохранены.');
      refresh();
    },
  });

  if (dishes.isPending) return <Loading />;
  if (dishes.error || !dishes.data)
    return <ErrorState error={dishes.error} onRetry={() => dishes.refetch()} />;

  const menus = dishes.data.menus;

  return (
    <>
      <Card title="Новое блюдо">
        {notice && <p className="faint">{notice}</p>}
        <Field label="Название">
          <input
            className="search"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Обязательное поле — остальное можно заполнить позже"
          />
        </Field>
        <Field label="Меню">
          <Select
            value={String(menuId ?? menus[0]?.id ?? '')}
            onChange={(event) => setMenuId(Number(event.target.value))}
          >
            {menus.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button
          variant="primary"
          disabled={name.trim().length === 0 || create.isPending}
          onClick={() => create.mutate()}
        >
          Завести черновик
        </Button>
      </Card>

      <Card title={`Карточки (${dishes.data.dishes.length})`}>
        {dishes.data.dishes.length === 0 ? (
          <Empty
            title="Справочник пуст"
            text="Заведите первое блюдо выше или загрузите файл меню — CSV или Excel."
          />
        ) : (
          dishes.data.dishes.map((row) => (
            <div key={row.id} className="dish-line">
              <div className="dish-line__name">
                {/* Управляющему видно, что карточка показная: её убирают
                    командой `make demo-study-clear`, а не правят руками. */}
                {row.is_demo && <span className="demo-badge">ДЕМО</span>}
                {row.name}
                {row.draft_of_id !== null && ' — правка опубликованной карточки'}
              </div>
              <div className="dish-line__meta">
                <Holes row={row} />
                {' · '}
                {row.status === 'published' ? 'опубликована' : 'черновик'}
                {row.status === 'draft' && (
                  <>
                    {' '}
                    <Button small onClick={() => publish.mutate(row.id)}>
                      Опубликовать
                    </Button>
                  </>
                )}{' '}
                <Button small variant="quiet" onClick={() => remove.mutate(row.id)}>
                  Снять
                </Button>
              </div>
            </div>
          ))
        )}
      </Card>

      <Card title="Что не заполнено">
        {report.isPending && <Loading />}
        {report.data && (
          <>
            <p className="faint">
              Карточек всего: {report.data.total_dishes}, с пробелами:{' '}
              {report.data.dishes.length}.
            </p>
            {report.data.unreviewed_ingredients.length > 0 && (
              <p className="faint">
                Ингредиенты без разметки аллергенов ({report.data.unreviewed_ingredients.length}
                ): {report.data.unreviewed_ingredients.map((item) => item.name).join(', ')}. Пока
                они не размечены, блюда с ними не попадают в фильтр «что можно без…».
              </p>
            )}
          </>
        )}
      </Card>
    </>
  );
}
